/**
 * <smootify-add-to-cart> Custom Element.
 *
 * Reproduit l'élément Smootify qui wrappe un <form> Webflow natif contenant
 * les variant selectors + quantity-input + buttons submit. Notre runtime
 * intercepte le submit AVANT que Webflow n'affiche son message
 * « Thank you! Your submission has been received! ».
 *
 * Structure Webflow attendue :
 *   <smootify-add-to-cart>
 *     <div class="form-block-product w-form">
 *       <form>
 *         <variant-swatches data-option="1">…</variant-swatches>
 *         <variant-selector data-option="2"><select>…</select></variant-selector>
 *         <quantity-input>…</quantity-input>
 *         <button type="submit" class="add-to-cart-button">Ajouter au panier</button>
 *         <button type="submit" name="buy-now" class="buy-now-button">Acheter</button>
 *       </form>
 *       <div class="w-form-done">Thank you! …</div>  ← jamais affiché
 *       <div class="w-form-fail">Oops …</div>
 *     </div>
 *   </smootify-add-to-cart>
 *
 * Actions :
 *   - Le bouton par défaut (sans name) déclenche add-to-cart.
 *   - Le bouton avec name="buy-now" déclenche add-to-cart + redirect checkout.
 *   - Le `data-state="loading"` est appliqué sur le bouton cliqué pour
 *     animer le spinner Webflow existant (.button-loading-state).
 */

import { runAddToCart } from "../attributes/action.js";
import { manageCommerceForm, syncProductFormButtons } from "../ui/commerce-form.js";
import type { Product, ProductVariant } from "../types.js";

interface SmootifyProductLike extends HTMLElement {
  product?: Product | null;
  selectedVariant?: ProductVariant | null;
}

function findHost(el: HTMLElement): SmootifyProductLike | null {
  return el.closest<SmootifyProductLike>("smootify-product");
}

// ---------------------------------------------------------------------------
//  Custom element
// ---------------------------------------------------------------------------

export class SmootifyAddToCartElement extends HTMLElement {
  private _form: HTMLFormElement | null = null;
  private _host: SmootifyProductLike | null = null;
  private _observer: MutationObserver | null = null;
  private _quantityEvents: AbortController | null = null;
  private _availabilityObserver: MutationObserver | null = null;

  connectedCallback(): void {
    // Le form est présent en tant qu'enfant statique dans le HTML exporté
    // Webflow — try immediately. Fallback MutationObserver au cas où Webflow
    // ré-injecterait le DOM dynamiquement (ex: CMS nested).
    this.tryAttach();
    if (!this._form) {
      const mo = this._observer = new MutationObserver(() => {
        if (this._form) return;
        this.tryAttach();
        if (this._form) mo.disconnect();
      });
      mo.observe(this, { childList: true, subtree: true });
    }
    // Écoute les variant_changed du smootify-product parent pour refléter
    // availableForSale sur l'état des boutons submit.
    const host = this._host = findHost(this);
    if (host) {
      host.addEventListener("smootify:variant_changed", this._syncAvailability);
      this._availabilityObserver = new MutationObserver(this._syncAvailability);
      this._availabilityObserver.observe(host, { attributes: true, attributeFilter: ["data-state"] });
      // Appliquer une fois au cas où le variant est déjà défini au moment du connect.
      this._syncAvailability();
    }
  }

  disconnectedCallback(): void {
    this._observer?.disconnect();
    this._observer = null;
    this._availabilityObserver?.disconnect();
    this._availabilityObserver = null;
    this._quantityEvents?.abort();
    this._quantityEvents = null;
    if (this._form) {
      this._form.removeEventListener("submit", this._onSubmit, true);
      this._form = null;
    }
    const host = this._host;
    if (host) {
      host.removeEventListener("smootify:variant_changed", this._syncAvailability);
    }
    this._host = null;
  }

  /**
   * Reflète variant.availableForSale sur les boutons submit : disabled +
   * class is-disabled (convention Smootify) quand la variant ne peut pas
   * être achetée. Les `<a condition="out-of-stock">` sont gérés séparément
   * par condition.ts — on s'occupe ici uniquement des buttons d'action.
   */
  private _syncAvailability = (): void => {
    if (!this._form) return;
    syncProductFormButtons(this._form);
  };

  private tryAttach(): void {
    const form = this.querySelector<HTMLFormElement>("form");
    if (!form || form === this._form) return;
    this._form = form;
    manageCommerceForm(form);
    // capture=true pour passer devant les handlers Webflow/jQuery natifs.
    form.addEventListener("submit", this._onSubmit, true);
    if (!form.hasAttribute("data-lmdr-managed")) {
      form.setAttribute("data-lmdr-managed", "true");
      // action="javascript:void(0)" : évite la navigation si notre handler rate.
      form.setAttribute("action", "javascript:void(0)");
      // noValidate : CRITIQUE. Le form Webflow a `<input name="field-4" required type="number">`
      // pour la quantité avec juste un placeholder "1" (pas une value). Sans
      // novalidate, HTML5 validation refuse le submit → l'event submit ne fire
      // JAMAIS → notre handler n'est jamais appelé. On bypass la validation
      // HTML5 (on la refait côté JS si besoin).
      form.noValidate = true;
    }
    // Pré-remplir l'input quantity avec 1 (ou son placeholder) si value vide,
    // pour que la UX reste cohérente (le user voit "1" dans le champ).
    const qInput = form.querySelector<HTMLInputElement>(
      'quantity-input input[type="number"], input[name="quantity"], input[type="number"]',
    );
    if (qInput && !qInput.value) {
      const ph = qInput.getAttribute("placeholder");
      qInput.value = ph && /^\d+$/.test(ph) ? ph : "1";
    }
    // Hide le w-form-done (success « Thank you! ») — défensif même si notre
    // preventDefault marche, au cas où un autre code essaierait de l'afficher.
    const wrapper = form.parentElement;
    const scope = wrapper ?? this;
    scope.querySelectorAll<HTMLElement>(".w-form-done, .w-form-fail").forEach((n) => {
      n.style.display = "none";
    });
    // Quantity input buttons +/-.
    this.attachQuantityHandlers();
    this._syncAvailability();
  }

  private attachQuantityHandlers(): void {
    if (!this._form) return;
    const qInput = this._form.querySelector<HTMLInputElement>(
      'quantity-input input[type="number"], input[name="quantity"]',
    );
    if (!qInput) return;
    this._quantityEvents?.abort();
    this._quantityEvents = new AbortController();
    const options = { signal: this._quantityEvents.signal };
    const scope = this._form;
    const plus = scope.querySelector<HTMLElement>('[data-action="plus"]');
    const minus = scope.querySelector<HTMLElement>('[data-action="minus"]');
    const stepBy = (delta: number): void => {
      const cur = Number.parseInt(qInput.value, 10);
      const base = Number.isFinite(cur) && cur > 0 ? cur : 1;
      const next = Math.max(1, base + delta);
      qInput.value = String(next);
      qInput.dispatchEvent(new Event("change", { bubbles: true }));
    };
    plus?.addEventListener("click", (e) => {
      e.preventDefault();
      stepBy(1);
    }, options);
    minus?.addEventListener("click", (e) => {
      e.preventDefault();
      stepBy(-1);
    }, options);
  }

  private _onSubmit = (ev: SubmitEvent): void => {
    // Empêche Webflow de passer et d'afficher « Thank you ».
    ev.preventDefault();
    ev.stopPropagation();
    ev.stopImmediatePropagation();

    const submitter = (ev.submitter as HTMLElement | null) ?? null;
    // buy-now détecté par : attribut name="buy-now" OU action="buy-now" OU class buy-now-button
    const isBuyNow =
      submitter?.getAttribute("name") === "buy-now" ||
      submitter?.getAttribute("action") === "buy-now" ||
      submitter?.classList.contains("buy-now-button") === true;

    // eslint-disable-next-line no-console
    console.log("[lmdr] smootify-add-to-cart submit", { isBuyNow, submitter });

    const button = submitter ?? this._form?.querySelector<HTMLElement>('button[type="submit"]') ?? this;
    void runAddToCart(button as HTMLElement, isBuyNow);
  };
}
